import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { LanguageUtils, TextWithTooltip } from '@pbt/pbt-ui-components'

import type {
  InventoryOrder,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import { LabelValue } from '~/components/common/labels/LabelValue'
import { getPluralPackageLabel } from '~/components/dashboard/admin/catalog/inventory/inventoryUtils'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'
import {
  getInventoryItemDisplayName,
  getInventoryItemDisplayQuantity,
} from '~/utils/inventoryItemUtils'

import InventoryOrderItemStatusLabel from '../InventoryOrderItemStatusLabel'
import { OrderItemFlags } from '../order-items/OrderItemFlags'
import { OrderCard } from '../OrderCard'

import styles from './OrderItemReceiptsPageHeader.module.scss'

interface OrderItemReceiptsPageHeaderProps {
  inventoryOrder: InventoryOrder
  inventoryOrderItem: InventoryOrderItem
}

export const OrderItemReceiptsPageHeader = ({
  inventoryOrder,
  inventoryOrderItem,
}: OrderItemReceiptsPageHeaderProps) => {
  const { t } = useTranslation()

  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const PackageType = useSelector(getPackageType)

  const productSizeUnit = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageUnits.id,
    ProductSizeUnit,
  )
  const packageType = getPluralPackageLabel(
    inventoryOrderItem.packageType.id,
    PackageType,
  )

  const { orderedQuantity, perPackageAmount } = inventoryOrderItem
  const orderQuantity = getInventoryItemDisplayQuantity({
    orderedQuantity,
    perPackageAmount,
    productSizeUnit,
    packageType,
  })

  const orderItemLabelValueProps = {
    ContainerProps: { className: styles.labelContainer },
    LabelProps: { className: styles.label },
    ValueProps: { className: styles.value },
  }

  return (
    <div className={styles.pageHeader}>
      <Box sx={{ mt: -2, ml: -3 }}>
        <OrderCard inventoryOrder={inventoryOrder} />
      </Box>
      <div className={styles.itemContainer}>
        <div className={styles.itemHeader}>
          <div>
            <span className={styles.title}>
              {getInventoryItemDisplayName(inventoryOrderItem.inventoryItem)}
            </span>
            <InventoryOrderItemStatusLabel
              statusId={inventoryOrderItem.status.id}
            />
            <OrderItemFlags
              controlled={inventoryOrderItem.inventoryItem.controlled}
              // @ts-expect-error - storeCold is not a property of BusinessCatalogItem yet
              storeCold={inventoryOrderItem.inventoryItem.storeCold}
            />
          </div>
          <TextWithTooltip
            className={styles.supplierName}
            tooltipText={t('Admin:CATALOG.ORDERS.ORDER_ITEM_SUPPLIER_NAME')}
          >
            {inventoryOrderItem.externalItemName}
          </TextWithTooltip>
        </div>

        <div className={styles.itemBody}>
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.QUANTITY_ORDERED_LABEL')}
            value={
              <>
                <span className={styles.highlight}>{orderQuantity.total}</span>{' '}
                <span>({orderQuantity.breakdown})</span>
              </>
            }
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.MANUFACTURER_LABEL')}
            value={inventoryOrderItem.manufacturer.name}
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.UPC_LABEL')}
            value={inventoryOrderItem.upc || '-'}
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.NDC_LABEL')}
            value={inventoryOrderItem.ndc || '-'}
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.INTERNAL_PART_NUMBER_LABEL')}
            value={inventoryOrderItem.inventoryItem.variation?.cumulusId || '-'}
          />
          <LabelValue
            {...orderItemLabelValueProps}
            label={t('Admin:CATALOG.ORDERS.SUPPLIER_SKU_LABEL')}
            value={inventoryOrderItem.sku}
          />
        </div>
      </div>
    </div>
  )
}
